import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ProduccionGuiaPrintDocument from '../../../components/Produccion/ProduccionGuiaPrintDocument';
import {
  PRODUCCION_GUIA_PRINT_PAGE_STYLES,
  PRODUCCION_GUIA_PRINT_STYLES,
} from '../../../components/Produccion/produccionGuiaPrintStyles';
import { useModuleSettings } from '../../../contexts/ModuleSettingsContext';
import { getGuiaConfigBySlug } from '../../../utils/produccionGuiaPaths';
import { loadProduccionGuiaInforme } from '../../../utils/loadProduccionGuiaInforme';
import { downloadProduccionGuiaPdf } from '../../../utils/generateProduccionGuiaPdf';

const GuiasProduccionPrint = () => {
  const { tipoSlug, id } = useParams();
  const config = getGuiaConfigBySlug(tipoSlug);
  const basePath = `/produccion/guias/${tipoSlug}`;
  const { produccionFabricarPepDesdePtEnabled } = useModuleSettings();

  const [informe, setInforme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [progressMsg, setProgressMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancel = false;
    if (!id) return undefined;

    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await loadProduccionGuiaInforme(id, {
          produccionFabricarPepDesdePtEnabled,
          onProgress: (msg) => {
            if (!cancel) setProgressMsg(msg);
          },
        });
        if (!cancel) setInforme(data);
      } catch (e) {
        if (!cancel) setError(e?.message || 'No se pudo cargar el informe');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();

    return () => {
      cancel = true;
    };
  }, [id, produccionFabricarPepDesdePtEnabled]);

  const handlePrint = () => window.print();

  const handleDownloadPdf = async () => {
    if (!informe || pdfLoading) return;
    setPdfLoading(true);
    try {
      await downloadProduccionGuiaPdf(informe);
    } catch (e) {
      alert(e?.message || 'No se pudo generar el PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  if (!config) {
    return (
      <div className="p-6">
        <p className="text-red-600">Tipo de guía no válido.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4" />
          <p className="text-gray-600">{progressMsg || 'Cargando informe de producción…'}</p>
        </div>
      </div>
    );
  }

  if (error || !informe) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 gap-3">
        <p className="text-red-600">{error || 'Informe no disponible'}</p>
        <Link to={basePath} className="text-teal-700 hover:underline text-sm">← Volver al listado</Link>
      </div>
    );
  }

  return (
    <div className="pg-print-page">
      <style>{`${PRODUCCION_GUIA_PRINT_PAGE_STYLES}${PRODUCCION_GUIA_PRINT_STYLES}`}</style>

      <div className="pg-print-toolbar no-print">
        <Link
          to={`${basePath}/analisis/${id}`}
          className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50"
        >
          ← Análisis
        </Link>
        <button
          type="button"
          onClick={handleDownloadPdf}
          disabled={pdfLoading}
          className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium shadow-md disabled:opacity-60 text-sm"
        >
          {pdfLoading ? 'Generando PDF…' : 'PDF'}
        </button>
        <button
          type="button"
          onClick={handlePrint}
          className="px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium shadow-md text-sm"
        >
          Imprimir
        </button>
      </div>

      <div className="pg-print-sheet">
        <ProduccionGuiaPrintDocument informe={informe} />
      </div>
    </div>
  );
};

export default GuiasProduccionPrint;
